import { FC, Fragment, useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  Link,
  Modal,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Theme } from '@mui/system';
import ConfigContext from 'src/context/config';
import UserContext from 'src/context/user';
import useNonNullContext from 'src/hooks/useNonNullContext';

import { useDiscount } from 'src/context/discount';
import PromoCodeBanner from 'src/components/PromoCodeBanner';
import {
  getProspectFormSchema,
  ProspectFormVariant,
} from '../../../utils/prospect-form-schema';

interface Props {
  isOpen: boolean;
  close: () => void;
  cartFunnelData?: { name?: string; email?: string; zip?: string };
  handleZipCodeModalSubmit: SubmitHandler<{
    zipCode: string;
    email?: string;
    name?: string;
  }>;
  isInvalidZipCode: boolean;
  isExternallyLoading: boolean;
  menuEntryOverride?: ProspectFormVariant;
  referralCode?: string;
  discountCode?: string;
  authenticated: boolean;
}

export const MODAL_WIDTH = '39rem';
const DEFAULT_MODAL_TITLE = `See what's available in your area`;
const DEFAULT_MODAL_SUBTITLE =
  'Enter your details to view meals that deliver to you.';

/*
 * Menu Entry Modal that can request different fields when landing on the /menu page based on tenant configuration or the menu entry override provided from the query params.

 * Menu Entry Modal Versions:
 * ZIP: Asks for only zip code (default if no menu entry version is specified)
 * EMAIL_ZIP: Asks for email and zip code
 * FULL: Asks for name, email, and zip code
 */

const ZipCodeModal: FC<Props> = ({
  isOpen,
  close,
  cartFunnelData,
  handleZipCodeModalSubmit,
  isInvalidZipCode,
  isExternallyLoading,
  menuEntryOverride,
  referralCode,
  discountCode: discountCodeProp,
  authenticated,
}) => {
  const { user } = useNonNullContext(UserContext);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm'),
  );
  const tenantConfig = useNonNullContext(ConfigContext);
  const { termsOfUse, privacyPolicy } = tenantConfig.globalLinks;

  const [showInvalidZipError, setShowInvalidZipError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Determine modal version, prioritizing an override from the URL query params. If neither are present, it will default to display only the zip code field.
  const modalVersion =
    menuEntryOverride || tenantConfig.menuPage?.menuEntryModalConfig?.version;

  // Get title and subtitle from tenant config or use defaults
  const modalTitle =
    tenantConfig.menuPage?.menuEntryModalConfig?.title || DEFAULT_MODAL_TITLE;
  const modalSubtitle =
    tenantConfig.menuPage?.menuEntryModalConfig?.subtitle ||
    DEFAULT_MODAL_SUBTITLE;

  const discountEligibility = useDiscount();
  const discountCodePublicDescription =
    discountEligibility.eligibleDiscount?.publicDescription;

  // Use discount code from context if available, else fall back to prop from query param or cart funnel data. This will be used to display the promo code banner in the zip code modal
  const discountCode =
    discountEligibility.eligibleDiscount?.discountCode || discountCodeProp;

  const { control, handleSubmit, watch } = useForm<FormData>({
    mode: 'onBlur',
    resolver: yupResolver(getProspectFormSchema(modalVersion)),
    defaultValues: {
      zipCode: cartFunnelData?.zip || '',
      email: cartFunnelData?.email || user?.email || '',
      name: cartFunnelData?.name || user?.name || '',
    },
  });

  const formValues = watch();
  const schema = getProspectFormSchema(modalVersion);
  // Check if the form is valid to enable or disable the continue button after all the fields have been filled. Validation errors will show on blur of each field.
  const isValid = schema.isValidSync(formValues);

  useEffect(() => {
    if (isInvalidZipCode) {
      setShowInvalidZipError(true);
    }
  }, [isInvalidZipCode]);

  /**
   * This handler is used to disable the modal button on submit and show a loading spinner
   */
  const handleZipCodeModalSubmitInternal = async (data: FormData) => {
    setIsLoading(true);
    try {
      await handleZipCodeModalSubmit(data);
    } catch (err: any) {
      if (err.message === 'Invalid zip') {
        setShowInvalidZipError(true);
      }
    }
    setIsLoading(false);
  };

  return (
    <Modal
      open={isOpen}
      onClose={() => {
        if (cartFunnelData?.zip) {
          close();
        }
      }}
      sx={{
        ['.MuiBackdrop-root']: {
          backdropFilter: 'blur(4px)',
        },
        boxShadow: 24,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '1rem',
      }}
    >
      <Box
        sx={{
          width: MODAL_WIDTH,
          padding: '2.1rem',
          margin: isSmallScreen ? undefined : '0 auto 2.75rem',
          backgroundColor: theme.palette.background.paper,
          borderRadius: '16px',
          '&:focus-visible': {
            outline: 'none',
          },
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        {/* Discount Banner */}
        {(referralCode || discountCode) && (
          <PromoCodeBanner
            discountCode={discountCode}
            discountCodePublicDescription={discountCodePublicDescription}
            referralCode={referralCode}
            authenticated={authenticated}
          />
        )}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            marginBottom: '2rem',
          }}
        >
          <Typography variant="h3" sx={{ mb: 1 }}>
            {modalTitle}
          </Typography>
          {modalSubtitle && (
            <Typography variant="body1">{modalSubtitle}</Typography>
          )}
        </Box>
        <FormControl>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
            }}
          >
            {/* Name field - FULL version only */}
            {modalVersion === 'FULL' && (
              <Controller
                control={control}
                name="name"
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    sx={{
                      width: '100%',
                      marginBottom: '1rem',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        backgroundColor: '#f8f9fa',
                      },
                    }}
                    label="Full Name"
                    variant="outlined"
                    disabled={isLoading || isExternallyLoading}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            )}
            {/* Email field - Display if EMAIL_ZIP and FULL  */}
            {(modalVersion === 'EMAIL_ZIP' || modalVersion === 'FULL') && (
              <Controller
                control={control}
                name="email"
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    sx={{
                      width: '100%',
                      marginBottom: '1rem',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        backgroundColor: '#f8f9fa',
                      },
                    }}
                    label="Email"
                    variant="outlined"
                    type="email"
                    // If a user is logged in the email field is disabled
                    disabled={isLoading || isExternallyLoading || !!user}
                    error={!!error}
                    helperText={
                      !!user
                        ? 'Email address is linked to your user account'
                        : error?.message
                    }
                  />
                )}
              />
            )}
            {/* The zipcode field will be shown in every scenario */}
            <Controller
              control={control}
              name="zipCode"
              render={({
                field: { onChange, ...field },
                fieldState: { error },
              }) => (
                <TextField
                  {...field}
                  autoFocus={modalVersion === 'ZIP'}
                  sx={{
                    width: '100%',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      backgroundColor: '#f8f9fa',
                    },
                  }}
                  label="Zip code"
                  variant="outlined"
                  disabled={isLoading || isExternallyLoading}
                  onChange={(e) => {
                    onChange(e);
                    if (showInvalidZipError) setShowInvalidZipError(false);
                  }}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
            <Box
              sx={{
                alignSelf: 'flex-start',
              }}
            >
              {showInvalidZipError && (
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.error.main,
                  }}
                >
                  Sorry, we do not deliver to this zip code.
                </Typography>
              )}
            </Box>
            <Box sx={{ marginTop: '1.5rem', width: '100%' }}>
              <Button
                // Enable button when form is valid (validation errors shown on blur)
                disabled={!isValid || isLoading || isExternallyLoading}
                size="large"
                variant="contained"
                onClick={handleSubmit(handleZipCodeModalSubmitInternal)}
                sx={{
                  width: '100%',
                  textTransform: 'none',
                }}
              >
                Continue
                {(isLoading || isExternallyLoading) && (
                  <Fragment>
                    <CircularProgress
                      sx={{
                        marginLeft: '0.5rem',
                        color: theme.palette.grey[500],
                      }}
                      size={20}
                    />
                  </Fragment>
                )}
              </Button>
            </Box>

            {/* Terms and Privacy Policy - Show only when an email field is present.*/}
            {modalVersion && modalVersion !== 'ZIP' && (
              <Grid
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: '1rem',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: '0.875rem',
                  }}
                >
                  By clicking above, you agree to{' '}
                  <Link
                    sx={{ color: theme.palette.text.secondary + '!important' }}
                    href={termsOfUse.url ?? '/terms'}
                    target={termsOfUse.target ?? '_blank'}
                  >
                    Terms of Use
                  </Link>{' '}
                  and consent to{' '}
                  <Link
                    sx={{ color: theme.palette.text.secondary + '!important' }}
                    href={privacyPolicy.url ?? '/privacy'}
                    target={privacyPolicy.target ?? '_blank'}
                  >
                    Privacy Policy.
                  </Link>
                </Typography>
              </Grid>
            )}
          </Box>
        </FormControl>
      </Box>
    </Modal>
  );
};

type FormData = {
  zipCode: string;
  email?: string;
  name?: string;
};

export default ZipCodeModal;
